import React, { useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { formatDateLessThanOneDay, formatCurrency } from "../../helper/utils";
import PaymentSummaryModal from "../paymentSummaryModal/PaymentSummaryModal";

const AttireModal = ({ isOpen, user, onClose, event }) => {
  const location = useLocation();
  const eventData = location.state?.eventDetails || event;

  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [selectedAttireOptions, setSelectedAttireOptions] = useState([]);
  const [quantities, setQuantities] = useState({});

  const [modalType, setModalType] = useState(null);

  const [isPaymentSummaryModalOpen, setIsPaymentSummaryModalOpen] =
    useState(false);
  const [paymentSummaryData, setPaymentSummaryData] = useState(null);

  const attireOptions = eventData?.eventAttire?.asoEbiImages || [];

  const totalAmount = useMemo(() => {
    return selectedAttireOptions.reduce((total, attire) => {
      const price = parseFloat(attire.price);
      const quantity = quantities[attire.attireTag] || 1;
      return total + price * quantity;
    }, 0);
  }, [selectedAttireOptions, quantities]);

  const formatCurrency = (amount) => {
    const num = typeof amount === "string" ? parseFloat(amount) : amount;

    if (num === 0) {
      return "Free";
    }

    const currency = eventData?.preferredCurrency || "NGN";
    const symbol = currency === "NGN" ? "₦" : "$";
    return `${symbol}${num.toLocaleString()}`;
  };

  if (!isOpen) return null;

  const handleSelectAttire = (attire, e) => {
    if (e) {
      e.stopPropagation();
    }

    setSelectedAttireOptions((prev) => {
      if (prev.find((item) => item.attireTag === attire.attireTag)) {
        return prev.filter((item) => item.attireTag !== attire.attireTag);
      } else {
        setQuantities((prevQuantities) => ({
          ...prevQuantities,
          [attire.attireTag]: 1,
        }));
        return [...prev, attire];
      }
    });
  };

  const handleQuantityChange = (attire, increment, e) => {
    if (e) {
      e.stopPropagation();
    }

    setQuantities((prev) => ({
      ...prev,
      [attire.attireTag]: Math.max(
        1,
        (prev[attire.attireTag] || 1) + (increment ? 1 : -1),
      ),
    }));
  };

  const handleDownload = (uri, e) => {
    e.stopPropagation();
    const link = document.createElement("a");
    link.href = uri;
    link.download = uri.split("/").pop();
    link.click();
  };

  const handleProceed = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!selectedAttireOptions.length || !deliveryAddress.trim()) {
      alert("Please select at least one attire and provide a delivery address");
      return;
    }

    const attireData = {
      type: "attire",
      selectedOptions: selectedAttireOptions,
      quantities: quantities,
      deliveryAddress: deliveryAddress,
      isAttirePurchase: true,
      applyServiceCharge: true,
    };

    setPaymentSummaryData(attireData);
    setIsPaymentSummaryModalOpen(true);
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  const capitalizeWords = (text = "") =>
    text
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

  return (
    <div className="other-purchase-modal-overlay" onClick={onClose}>
      <div
        className="other-purchase-modal-content attire-modal-large"
        onClick={handleModalClick}
      >
        <button onClick={onClose} className="modal-close-button" type="button">
          <span>×</span>
        </button>
        <div className="modal-body">
          <h2 className="purchase-title">Select Event Attire</h2>
          <p className="purchase-description">
            Choose your preferred attire. Multiple selections allowed.
          </p>

          <div className="attire-options">
            {attireOptions.map((attire, index) => (
              <div
                // key={index}
                key={attire.attireTag}
                className={`attire-option ${
                  selectedAttireOptions.find(
                    (item) => item.attireTag === attire.attireTag,
                  )
                    ? "selected"
                    : ""
                }`}
                onClick={(e) => handleSelectAttire(attire, e)}
              >
                <button
                  type="button"
                  className="download-button"
                  onClick={(e) => handleDownload(attire.uri, e)}
                >
                  <img
                    src={"/images/download-icon.png"}
                    alt="Download"
                    className="download"
                  />
                  Download
                </button>

                {selectedAttireOptions.find(
                  (item) => item.attireTag === attire.attireTag,
                ) && <span className="check-marked">✓</span>}

                <img
                  src={attire.uri || "/images/asoebi.png"}
                  alt={attire.attireTag}
                  className="modal-attire-image"
                />
                <p>
                  <span className="attire-payment-name">
                    {capitalizeWords(attire.attireTag)}
                  </span>
                </p>

                <p>{formatCurrency(attire.price)}</p>
              </div>
            ))}
          </div>

          {selectedAttireOptions.length > 0 && (
            <div className="selected-attire-details">
              {selectedAttireOptions.map((attire, index) => (
                <div key={index} className="attire-details-row">
                  <h3>{capitalizeWords(attire.attireTag)}</h3>
                  <div className="attire-price-container">
                    <div className="quantity-control">
                      <p>
                        {formatCurrency(
                          parseFloat(attire.price) *
                            (quantities[attire.attireTag] || 1),
                        )}
                      </p>
                      <button
                        type="button"
                        className="quantity-button"
                        onClick={(e) => handleQuantityChange(attire, false, e)}
                        disabled={quantities[attire.attireTag] <= 1}
                      >
                        −
                      </button>
                      <span className="quantity-text">
                        {quantities[attire.attireTag] || 1}
                      </span>
                      <button
                        type="button"
                        className="quantity-button"
                        onClick={(e) => handleQuantityChange(attire, true, e)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <div className="total-attire-details-row">
                <p className="total-price-label">Total: </p>
                <p className="total-price-style">
                  {formatCurrency(totalAmount)}
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleProceed}>
            <div className="delivery-address-container">
              <label className="delivery-address-label">
                Delivery Address *
              </label>
              <input
                type="text"
                placeholder="Enter delivery address"
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                className="address-input"
              />
              <p className="mandatory-address-note">
                Provide the address where you want your attire delivered
              </p>

              {eventData?.eventAttire?.refundDeadlineDate && (
                <p className="mandatory-refund-policy">
                  Refund Deadline Date:{" "}
                  {formatDateLessThanOneDay(
                    eventData.eventAttire.refundDeadlineDate,
                  )}
                </p>
              )}
            </div>

            <div className="modal-controls">
              <button
                type="button"
                onClick={onClose}
                className="modal-button other-purchase-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="modal-button proceed-button"
                disabled={
                  !selectedAttireOptions.length || !deliveryAddress.trim()
                }
              >
                Proceed to Payment
              </button>
            </div>
          </form>
        </div>
      </div>

      {isPaymentSummaryModalOpen && paymentSummaryData && (
        <PaymentSummaryModal
          isOpen={isPaymentSummaryModalOpen}
          onClose={() => {
            setIsPaymentSummaryModalOpen(false);
            setPaymentSummaryData(null);
          }}
          onBack={() => {
            setIsPaymentSummaryModalOpen(false);
            if (paymentSummaryData.type === "cashGift") {
              setModalType("cashGift");
            } else if (paymentSummaryData.type === "attire") {
              setModalType("attire");
            }
          }}
          event={eventData}
          selectedOptions={paymentSummaryData.selectedOptions}
          deliveryAddress={paymentSummaryData.deliveryAddress}
          quantities={paymentSummaryData.quantities}
          isAttirePurchase={paymentSummaryData.isAttirePurchase}
          applyServiceCharge={paymentSummaryData.applyServiceCharge}
          user={user}
        />
      )}
    </div>
  );
};

export default React.memo(AttireModal);
